import _ from "lodash";
import {Formik, getIn} from "formik";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { PrimaryButton } from "../../../../../common/components/CustomButton";
import { FluidRadioGroup } from "../../../../../common/components/FluidRadioGroup";
import { noop } from "../../../../../common/utils/operations";
import {
	ClickUnit,
	ContractPeriodType,
	ContractDetailType,
} from "../../../../../requests_cm/gecoContractsService/types";
import { Box, CircularProgress } from "@mui/material";
import { style } from "./ClickModal.style";
import { FluidButton } from "../../../../../common/components/FluidButton";
import { clickValidator } from "../../../formik/clickabilityTabValidators";
import {
	ClickFormik,
	clickFormikInitalValueMapper,
} from "../../../formik/clickabilityTabFormik";
import { Spacer } from "../../../../../common/components/Spacer";
import { FluidTextField } from "../../../../../common/components/FluidTextField";
import { FluidNumber } from "../../../../../common/components/FluidNumberField";
import FluidDatePicker from "../../../../../common/components/FluidDatePicker";
import {
	ClickTypesResponseType,
	OtClickPeriods,
} from "../../../../../requests_cm/gecoReferentialService/types";
import { useCallback, useMemo, useState } from "react";
import { If } from "../../../../../common/components/If";
import { ErrorText } from "../../../../../common/components/ErrorText";
import Group from "../../../../../common/components/Group";
import FluidSelect from "../../../../../common/components/FluidSelect";
import {
	MapDayMonthYearToYearMonthDay,
	mapYearMonthDayToDayMonthYear,
	TimePeriodOptions,
	timePeriodOptions,
} from "../../../../../common/utils/timePeriodOptions";
import type { NewClickOverviewT } from "../OverviewModal/types";
import { differenceInHours } from "date-fns";
import {zonedTimeToUtc} from "date-fns-tz";

interface ClickModalProps {
	isOpen: boolean;
	createClickErrorMessage: string | string[];
	contractPeriod: ContractPeriodType;
	contract: ContractDetailType;
	clickPeriods: ClickTypesResponseType[];
	isLoading?: boolean;
	handleClose: () => void;
	onCreateClick: (click: ClickFormik) => void;
	newClickUuid: string;
	newClickOverview: NewClickOverviewT | undefined;
	totalVolumeAbsolute: string;
}

// Helper to calculate hours between two dates (YYYY-MM-DD)
const calculateHours = (start: string | undefined,end: string | undefined, timezone:string | undefined ) => {
	if (!start || !end || !timezone) return 0;
	return differenceInHours(zonedTimeToUtc(end, timezone), zonedTimeToUtc(start,timezone));
};

export default function ClickModal(props: ClickModalProps) {
	const {
		isOpen,
		createClickErrorMessage,
		contractPeriod,
		contract,
		clickPeriods,
		isLoading,
		handleClose,
		onCreateClick,
		newClickUuid,
		newClickOverview,
		totalVolumeAbsolute
	} = props;
	// To allow traders to submit clicks one after the other in rapid succession
	const [formikHistory, setFormikHistory] = useState<
		ClickFormik | undefined
	>();

	const clickTypeOptions = useMemo(() => {
		return clickPeriods.map((clickType) => ({
			value: `${clickType.id}`,
			label: `${clickType.name[0].toUpperCase()}${clickType.name
				.substring(1)
				.toLowerCase()}`,
		}));
	}, [clickPeriods]);

	// fixing multi clickability. Will be removed when full feature developed.
	const firstClickability = useMemo(() => {
		if (
			contractPeriod?.clickability &&
			contractPeriod?.clickability.length > 0
		) {
			return contractPeriod?.clickability[0];
		}
	}, [contractPeriod?.clickability]);
	const maxCapacity = firstClickability?.max_capacity ?? 0;
	const cpDurationHours = contractPeriod?.start_date && contractPeriod?.end_date && contract?.timezone ? calculateHours(contractPeriod.start_date, contractPeriod.end_date, contract.timezone) : 0;
	const clickPeriod: OtClickPeriods | undefined = useMemo(
		() =>
			firstClickability?.clickability_information?.click_period_frequency,
		[contractPeriod]
	);

	const memoTimePeriodOptions: TimePeriodOptions = useMemo(() => {
		if (contractPeriod?.start_date && contractPeriod?.end_date) {
			return timePeriodOptions(
				mapYearMonthDayToDayMonthYear(contractPeriod.start_date),
				mapYearMonthDayToDayMonthYear(contractPeriod.end_date),
				true
			);
		}
		return {
			[OtClickPeriods.CalYear]: [],
			[OtClickPeriods.Month]: [],
			[OtClickPeriods.Quarter]: [],
			[OtClickPeriods.TradePeriod]: [],
		};
	}, [contractPeriod]);

	const getClickPeriodFromId = useCallback(
		(id: string | undefined): undefined | OtClickPeriods => {
			const value = _.find(
				clickPeriods || [],
				// @ts-ignore
				(clickType) => clickType.id === Number(id)
			)?.ot_name;
			return value;
		},
		[clickPeriods]
	);

	const getPeriodIdFromClickType = useCallback(
		(type: OtClickPeriods | undefined): undefined | string => {
			const value = _.find(
				clickPeriods || [],
				// @ts-ignore
				(clickType) => clickType.ot_name === type
			)?.id;
			return value;
		},
		[clickPeriods]
	);

	const initialValues = useMemo(
		() =>
			clickFormikInitalValueMapper(
				contractPeriod,
				contract,
				getPeriodIdFromClickType(clickPeriod),
				newClickUuid,
				formikHistory,
				newClickOverview
			),
		[formikHistory, contractPeriod,contract, newClickOverview, newClickUuid]
	);

	const isMWClickable = useCallback(
		(values:ClickFormik) => contractPeriod?.processing_type == "PPA_PHYSICAL_INDEXED_CLICKABLE" && values.click_unit === ClickUnit.MWH,
		[contractPeriod?.processing_type]
	);

	const onSubmit = useCallback(
		(formikData: ClickFormik) => {
			setFormikHistory(formikData);
			onCreateClick({
				...formikData,
				volume: isMWClickable(formikData) && formikData.volume &&formikData.periodHours  ? formikData.volume * formikData.periodHours : formikData.volume,
			});
		},
		[
			formikHistory,
			contractPeriod,
			onCreateClick,
			isMWClickable,
		]
	);

	return (
		<Dialog maxWidth="lg" onClose={handleClose} open={isOpen}>
			<DialogTitle>{"Create Click"}</DialogTitle>
			<Formik
				validateOnChange={false}
				validateOnBlur={false}
				validationSchema={() => {
					return clickValidator();
				}}
				enableReinitialize
				initialValues={initialValues}
				onSubmit={onSubmit}
			>
				{({
					handleChange,
					handleSubmit,
					setFieldValue,
					values,
					errors,
				}) => (
					<form onSubmit={handleSubmit}>
						<DialogContent>
							<Box sx={style.container}>
								<FluidRadioGroup
									orientation="row"
									name={"click_period_id"}
									errorMessage={getIn(
										errors,
										"click_period_id"
									)}
									value={`${values.click_period_id}`}
									title={"Click Period"}
									onChange={noop}
									options={clickTypeOptions}
									isDisabled
								/>

								<Spacer gap={24} />

								<Group>
									<If
										condition={[
											OtClickPeriods.Month,
											OtClickPeriods.Quarter,
											OtClickPeriods.CalYear,
										].includes(
											getClickPeriodFromId(
												values.click_period_id
											) as OtClickPeriods
										)}
									>
										<FluidSelect
											items={
												_.get(
													memoTimePeriodOptions,
													getClickPeriodFromId(
														values.click_period_id
													) || ""
												) || []
											}
											label="Horizon"
											name="period"
											// @ts-ignore
											onChange={(val: string) => {
												const [startDate, endDate] =
													val.split("@");
												const clickStart =
													MapDayMonthYearToYearMonthDay(
														startDate
													);
												const clickEnd =
													MapDayMonthYearToYearMonthDay(
														endDate
													);
												setFieldValue("period", val);

												setFieldValue(
													"click_start",
													clickStart
												);

												setFieldValue(
													"click_end",
													clickEnd
												);
												setFieldValue(
													"periodHours",
													calculateHours(clickStart, clickEnd, contract?.timezone)
												);
											}}
											isLabelStatic
											errorMessage={getIn(
												errors,
												"period"
											)}
											//@ts-ignore
											value={values.period}
										/>
									</If>

									<FluidDatePicker
										onChange={handleChange}
										name={"click_start"}
										label={"Start Date"}
										isReadOnly={
											OtClickPeriods.TradePeriod !=
											getClickPeriodFromId(
												values.click_period_id
											)
										}
										value={values.click_start}
										errorMessage={errors.click_start}
									/>

									<FluidDatePicker
										onChange={handleChange}
										name={"click_end"}
										label={"End Date"}
										isReadOnly={
											OtClickPeriods.TradePeriod !=
											getClickPeriodFromId(
												values.click_period_id
											)
										}
										value={values.click_end}
										errorMessage={errors.click_end}
									/>
									<Box sx={{ width: "120px", marginLeft: 2 }}>
										<FluidTextField
											label="Hours in Period"
											value={`${values.periodHours}`}
											name="periodHours"
											isDisabled
											onChange={handleChange}
										/>
									</Box>
								</Group>
								<Spacer gap={24} />
								<FluidTextField
									onChange={handleChange}
									value={values.name}
									name={"name"}
									label={"Name"}
									errorMessage={getIn(errors, "name")}
								/>
								<Spacer gap={24} />
								<Group>
									<FluidNumber
										onChange={handleChange}
										errorMessage={getIn(errors, "price")}
										name={"price"}
										title={"Price"}
										value={
											values.price as unknown as number
										}
									/>
									<FluidNumber
										onChange={handleChange}
										errorMessage={getIn(
											errors,
											"mid_price"
										)}
										name={"mid_price"}
										title={"Mid Price"}
										value={
											values.mid_price as unknown as number
										}
									/>
								</Group>
								<Spacer gap={24} />
								<Group>
									<If
										condition={
											values.click_unit === undefined ||
											values.click_unit === ClickUnit.MWH
										}
									>
										<FluidNumber
											onChange={handleChange}
											errorMessage={getIn(
												errors,
												"volume"
											)}
											name={"volume"}
											title={"Volume"}
											value={
												values.volume as unknown as number
											}
										/>

										<Box sx={{ width: "180px"}}>
										<PrimaryButton
										onClick={()=> {
										setFieldValue("volume", maxCapacity * cpDurationHours - Number(totalVolumeAbsolute) );
										}}
										text="Add remaining volume"
										type="button"
										color="secondary"
										style={{ marginTop: 27 }}
										/>
										</Box>
									</If>
									<If
										condition={
											values.click_unit ===
											ClickUnit.PERCENTAGE
										}
									>
										<Box sx={{ width: "162px" }}>
											<FluidNumber
												title="Volume"
												name="volumePct"
												onChange={handleChange}
												errorMessage={getIn(
													errors,
													"volumePct"
												)}
												value={getIn(
													values,
													"volumePct"
												)}
											/>
										</Box>

									</If>
									<Box sx={{ width: "180px", marginLeft: 2 }}>
										<If condition={isMWClickable(values)}>
											<FluidTextField
												label="Volume x Hours"
												value={`${Number(values.volume) * Number(values.periodHours)}`}
												name="volumeHours"
												isDisabled
												onChange={handleChange}
											/>
										</If>
									</Box>
									<FluidRadioGroup
										orientation="row"
										name={"click_unit"}
										errorMessage={getIn(
											errors,
											"click_unit"
										)}
										value={`${values.click_unit}`}
										title={"Click Unit"}
										onChange={(value: unknown | string) =>
											setFieldValue("click_unit", value)
										}
										options={[
											{
												label: ClickUnit.MWH,
												value: ClickUnit.MWH,
											},
											{
												label: ClickUnit.PERCENTAGE,
												value: ClickUnit.PERCENTAGE,
											},
										]}
									/>
								</Group>
								<Spacer gap={24} />
								<FluidDatePicker
									onChange={handleChange}
									name={"click_date"}
									label={"Click Date"}
									value={values.click_date}
									errorMessage={getIn(errors, "click_date")}
								/>
								<Spacer gap={24} />
								<If condition={!!createClickErrorMessage}>
									<ErrorText>
										{createClickErrorMessage}
									</ErrorText>
									<Spacer gap={24} />
								</If>
							</Box>
						</DialogContent>

						<DialogActions>
							<If condition={!isLoading}>
								<PrimaryButton
									onClick={handleClose}
									text="Cancel"
									type="button"
									color="secondary"
								/>
								<FluidButton
									type="submit"
									icon={"add_circle_outline"}
									label={"Add"}
									onClick={noop}
								/>
							</If>
							<If condition={!!isLoading}>
								<CircularProgress />
							</If>
						</DialogActions>
					</form>
				)}
			</Formik>
		</Dialog>
	);
}
